import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../hooks/useAuth';
import { canEditWine } from '@wineryxplorer/shared';
import { PageContainer } from '../../components/PageContainer';
import { ImageModal } from '../../components/ui/ImageModal';
import { WarningTriangleIcon } from '../../components/ui/icons';
import { ReportErrorLink } from '../../components/ReportErrorLink';
import type { Media, Wine, Venue, PopulatedWine } from '@wineryxplorer/shared';
import { WineDetailLoading } from './WineDetailLoading';
import { WineDetailError } from './WineDetailError';
import { WineHeader } from './WineHeader';
import { WineQuickInfo } from './WineQuickInfo';
import { WineDescription } from './WineDescription';
import { WineReviews } from './WineReviews';
import { WineTastingNotes } from './WineTastingNotes';
import { getBackNavigation, isNetworkError } from './utils';

export const WineDetail = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  const { data: wineData, isPending, error, refetch } = trpc.wine.getById.useQuery(
    { id: id || '' },
    { enabled: !!id, retry: false }
  );

  // Type assertion for the populated wine data
  const wine = wineData as PopulatedWine | undefined;

  // Get venue data for permission checking
  const { data: venueData } = trpc.venue.getById.useQuery(
    { id: wine?.venueId?._id || '' },
    { enabled: Boolean(wine?.venueId?._id), retry: false }
  );

  // Fetch all media items by their IDs from the wine's imageIds array
  const wineImageIds = wine?.imageIds || [];
  
  // Use getByIds to fetch all images at once (avoids hooks violation)
  const { data: wineImagesData = [], isPending: isMediaPending, error: mediaError } = trpc.media.getByIds.useQuery(
    { ids: wineImageIds },
    { enabled: wineImageIds.length > 0, retry: false }
  );

  // Type the response properly as Media array
  const wineImages: Media[] = wineImagesData as Media[];

  // Fetch hero image separately if wine has heroImageId
  const { data: heroImageData } = trpc.media.getById.useQuery(
    { id: wine?.heroImageId || '' },
    { enabled: Boolean(wine?.heroImageId), retry: false }
  );

  // Handle retry functionality
  const handleRetry = async () => {
    setRetryCount(prev => prev + 1);
    setIsRetrying(true);
    try {
      await refetch();
    } catch (error) {
      // Error will be handled by the query error states
      console.error('Retry failed:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  // Handle image modal
  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Determine appropriate back navigation destination
  const backNavigation = getBackNavigation(location, wine);

  if (isPending) {
    return <WineDetailLoading />;
  }

  if (error) {
    return (
      <WineDetailError
        error={error}
        backNavigation={backNavigation}
        onRetry={handleRetry}
        isRetrying={isRetrying}
        retryCount={retryCount}
        isNetworkError={isNetworkError}
      />
    );
  }

  if (!wine) {
    return (
      <div className="px-0 md:px-6 lg:px-8 pt-2 pb-4 max-w-4xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto">
        <PageContainer className="mt-2">
          <div className="text-center py-8 md:py-12 px-4">
            <div className="mx-auto flex items-center justify-center h-14 w-14 md:h-16 md:w-16 rounded-full bg-primary-50 mb-4">
              <WarningTriangleIcon className="h-7 w-7 md:h-8 md:w-8 text-primary-700" />
            </div>
            <h1 className="mb-2">Wine Not Found</h1>
            <p className="text-gray-600 mb-6 px-2">
              The wine you're looking for doesn't exist or may have been removed.
            </p>
            <div className="space-y-3">
              <a
                href="/wines"
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm md:text-base font-medium rounded-md shadow-sm text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 min-h-[44px] touch-manipulation"
              >
                Browse All Wines
              </a>
              <div>
                <a
                  href="/wineries"
                  className="inline-flex items-center px-6 py-3 border border-gray-300 text-sm md:text-base font-medium rounded-md text-gray-800 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 min-h-[44px] touch-manipulation"
                >
                  Explore Wineries
                </a>
              </div>
            </div>
          </div>
        </PageContainer>
      </div>
    );
  }

  // Check if user can edit this wine
  const canEdit = user && venueData && canEditWine(
    { ...wine, venueId: wine.venueId?._id || '' } as Wine, 
    user.role, 
    user.id, 
    venueData as unknown as Venue
  );

  // Handle edit button click
  const handleEditClick = () => {
    navigate(`/wines/${id}/edit`);
  };

  return (
    <div className="px-0 md:px-6 lg:px-8 pt-2 pb-4 max-w-4xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto">
      <PageContainer className="space-y-10 md:space-y-14 mt-2">
        <WineHeader
          wine={wine}
          wineImages={wineImages}
          heroImageData={heroImageData as Media | undefined}
          showEditButton={Boolean(canEdit)}
          onEditClick={handleEditClick}
        />
        <WineDescription wine={wine} />
        <WineQuickInfo wine={wine} />

        {/* Gallery Images */}
        {wineImages.length > 0 && (
          <section>
            <h2 className="font-sans text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">Gallery</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {wineImages.slice(0, 6).map((media, index) => (
                <div
                  key={media.id}
                  className="aspect-square overflow-hidden rounded-lg cursor-pointer"
                  onClick={() => handleImageClick(index)}
                >
                  <img
                    src={media.cloudinaryUrl}
                    alt={media.caption || 'Wine image'}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Tasting Notes Section */}
        <WineTastingNotes wineId={id || ''} wineName={wine.name} />

        {/* Reviews Section */}
        <WineReviews wineId={id || ''} wineName={wine.name} />

        <div className="pt-4 border-t border-gray-200">
          <ReportErrorLink entityType="wine" entityId={wine._id} entityName={wine.name} />
        </div>
      </PageContainer>

      {/* Image Modal */}
      <ImageModal
        images={wineImages}
        initialIndex={selectedImageIndex}
        isOpen={modalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};